import { useState, FormEvent } from 'react';
import type { CashierUser, CreateCashierRequest, UpdateCashierRequest } from '@photobooth/api-client';
import type { Outlet } from '@photobooth/types';

interface CashierFormProps {
  cashier?: CashierUser;
  outlets: Outlet[];
  onSubmit: (data: CreateCashierRequest | UpdateCashierRequest) => Promise<void>;
  onCancel: () => void;
}

export function CashierForm({ cashier, outlets, onSubmit, onCancel }: CashierFormProps) {
  const [formData, setFormData] = useState({
    username: cashier?.username || '',
    password: '',
    email: cashier?.email || '',
    fullName: cashier?.fullName || '',
    outletIds: cashier?.outletIds || [] as string[],
    isActive: cashier?.isActive ?? true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }

    if (!cashier && !formData.password) {
      setError('Password is required for new cashiers');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!cashier && formData.outletIds.length === 0) {
      setError('At least one outlet must be selected');
      return;
    }

    setIsSubmitting(true);

    try {
      if (cashier) {
        // Update existing cashier
        const data: UpdateCashierRequest = {
          username: formData.username !== cashier.username ? formData.username : undefined,
          email: formData.email !== cashier.email ? formData.email : undefined,
          fullName: formData.fullName !== cashier.fullName ? formData.fullName : undefined,
          isActive: formData.isActive !== cashier.isActive ? formData.isActive : undefined,
        };

        // Only include password if it was changed
        if (formData.password) {
          data.password = formData.password;
        }

        // Remove undefined fields
        Object.keys(data).forEach(key => {
          if (data[key as keyof UpdateCashierRequest] === undefined) {
            delete data[key as keyof UpdateCashierRequest];
          }
        });

        await onSubmit(data);
      } else {
        // Create new cashier
        const data: CreateCashierRequest = {
          username: formData.username,
          password: formData.password,
          email: formData.email || undefined,
          fullName: formData.fullName || undefined,
          outletIds: formData.outletIds,
        };

        await onSubmit(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save cashier');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="username" className="block text-sm font-medium text-text mb-1">
          Username *
        </label>
        <input
          id="username"
          type="text"
          required
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="cashier123"
          minLength={3}
          maxLength={100}
        />
        <p className="text-sm text-text-muted mt-1">
          3-100 characters, used for login
        </p>
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
          Password {!cashier && '*'}
        </label>
        <input
          id="password"
          type="password"
          required={!cashier}
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={cashier ? 'Leave blank to keep current password' : 'Enter password'}
          minLength={6}
        />
        <p className="text-sm text-text-muted mt-1">
          {cashier 
            ? 'Leave blank to keep current password. Minimum 6 characters if changing.'
            : 'Minimum 6 characters'
          }
        </p>
      </div>

      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-text mb-1">
          Full Name
        </label>
        <input
          id="fullName"
          type="text"
          value={formData.fullName}
          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="John Doe"
          maxLength={255}
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-text mb-1">
          Email
        </label>
        <input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="cashier@example.com"
        />
      </div>

      {!cashier && (
        <div>
          <label className="block text-sm font-medium text-text mb-2">
            Outlet Access *
          </label>
          <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
            {outlets.length === 0 ? (
              <p className="text-sm text-text-muted">No outlets available</p>
            ) : (
              outlets.map((outlet) => (
                <label
                  key={outlet.outletId}
                  className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.outletIds.includes(outlet.outletId)}
                    onChange={() => {
                      setFormData((prev) => ({
                        ...prev,
                        outletIds: prev.outletIds.includes(outlet.outletId)
                          ? prev.outletIds.filter((id) => id !== outlet.outletId)
                          : [...prev.outletIds, outlet.outletId],
                      }));
                    }}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-text font-medium">{outlet.name}</span>
                    <span className="text-sm text-text-muted ml-2">({outlet.location})</span>
                  </div>
                </label>
              ))
            )}
          </div>
          <p className="text-sm text-text-muted mt-1">
            Select which outlets this cashier can access
          </p>
        </div>
      )}

      {cashier && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-text">
            Active (can log in)
          </label>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : cashier ? 'Update Cashier' : 'Create Cashier'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 bg-gray-200 text-text py-2 px-4 rounded-md hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
